import { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'clsx';
import map from 'lodash/map';
import get from 'lodash/get';
import flow from 'lodash/flow';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { validateAndGetHexColor } from 'src/common/utils/color';
import {
  logCategoryActionLabel,
  logWebActivity,
} from 'src/common/actions/logging';
import cssVariables from 'src/common/styles/variables';
import web from '../../../constants/analytics/categoryActionLabel/web';
import withGuideItemTracking from '../../../decorators/containerItems/withGuideItemTracking';

import ActionButton from '../../shared/button/ActionButton';
import CloseButton from '../../shared/button/CloseButton';

import {
  imageAlignments,
  imageSizes,
  marginStyles,
} from '../../../constants/styles';
import { LINE_BREAK_INSTRUCTION } from '../../../constants/localizations/shared';

import css from './super-prompt.module.scss';

const PROMPT = 'prompt';

const imageSizeCss = {
  [imageSizes.Small]: css.imageSmall,
};

// exported for testing
export class SuperPrompt extends Component {
  static propTypes = {
    guideItem: PropTypes.object.isRequired,
    promptActions: PropTypes.object.isRequired,
    getStyles: PropTypes.func.isRequired,
    trackingRef: PropTypes.object.isRequired,
    closeGuideItem: PropTypes.func.isRequired, // unmounts the component along with Event Listener
  };

  static defaultProps = {
    getStyles: () => ({}),
  };

  constructor(props) {
    super(props);

    this.state = {
      isHidden: false,
    };
    this.promptContainerHeight = null;
    this.onClose = this.onClose.bind(this);
    this.onXClose = this.onXClose.bind(this);
  }

  componentDidMount() {
    this.props.promptActions.logWebActivity(web.actions.show, PROMPT);
  }

  onClose(label) {
    const { guideItem, promptActions, trackingRef } = this.props;
    if (trackingRef) {
      this.promptContainerHeight = trackingRef.current.offsetHeight;
    }
    this.setState({
      isHidden: true,
    });
    const itemToken = get(guideItem, 'context.token');
    const reportData = get(guideItem, 'actions.dismiss.reportData', '');
    promptActions.logWebActivity(web.actions.tap, label);
    promptActions.logCategoryActionLabel(reportData, { itemToken });
  }

  onXClose() {
    this.onClose(`${PROMPT}-xClose`);
  }

  getPromptContainerProps(guideItem, itemStyles, dismissAction) {
    const { backgroundColor, margins } = itemStyles;
    const promptContainerProps = {
      style: {
        backgroundColor: validateAndGetHexColor(
          backgroundColor,
          cssVariables['--grey-3'],
        ),
        maxHeight: this.promptContainerHeight,
      },
    };

    const backgroundImage = get(
      guideItem,
      'properties.backgroundImage.imageUrl',
    );
    if (backgroundImage) {
      promptContainerProps.style.backgroundImage = `url(${backgroundImage})`;
    }

    promptContainerProps.className = classNames(css.promptContainer, {
      [css.hidden]: this.state.isHidden,
      [css.withCloseIcon]: dismissAction,
      [css.noMargins]: marginStyles[margins],
    });

    return promptContainerProps;
  }

  render() {
    const { guideItem, getStyles, trackingRef, closeGuideItem } = this.props;
    const { title = '', subtitle, image, actions, style } = guideItem;
    const itemStyles = getStyles(style);
    const {
      textColor,
      imageAlignment,
      imageSize,
      titleTextColor,
      titleTextSecondaryColor,
      titleTextBackgroundColor,
      titleTextFont,
      titleTextFontSize,
      titleTextTopMargin,
      titleTextBottomMargin,
      subtitleTextFont,
    } = itemStyles;

    const buttonsArray = get(actions, 'navigate.destinations', []);
    const dismissAction = get(actions, 'dismiss');
    const textColorToUse = validateAndGetHexColor(
      textColor,
      cssVariables['--secondary-color-5'],
    );
    const [primaryTitle, secondaryTitle] = title.split(LINE_BREAK_INSTRUCTION);
    const isImageOnLeft = imageAlignment === imageAlignments.Left;

    const titleContainerClassNames = classNames({
      [css[`titleContainerTopMargin${titleTextTopMargin}`]]: titleTextTopMargin,
      [css[`titleContainerBottomMargin${titleTextBottomMargin}`]]:
        titleTextBottomMargin,
    });
    const titleClassNames = classNames(css.title, {
      [css.multiLineTitle]: secondaryTitle,
      [css[`titleSize${titleTextFontSize}`]]: titleTextFontSize,
      [css[`titleFont${titleTextFont}`]]: titleTextFont,
    });
    const primaryTitleStyles = {
      color: validateAndGetHexColor(titleTextColor, textColorToUse),
      backgroundColor: validateAndGetHexColor(
        titleTextBackgroundColor,
        'transparent',
      ),
    };
    const secondaryTitleStyles = {
      color: validateAndGetHexColor(titleTextSecondaryColor, textColorToUse),
      backgroundColor: validateAndGetHexColor(
        titleTextBackgroundColor,
        'transparent',
      ),
    };
    const subtitleClassNames = classNames(css.subtitle, {
      [css[`subtitleFont${subtitleTextFont}`]]: subtitleTextFont,
    });

    return (
      <div
        {...this.getPromptContainerProps(guideItem, itemStyles, dismissAction)}
        data-testid="superPrompt"
        ref={trackingRef}
        onAnimationEnd={closeGuideItem}
      >
        <div
          className={classNames(css.textAndButtonsContainer, {
            [css.onLeftSide]: !isImageOnLeft,
          })}
        >
          <div data-testid="textContainer">
            <div
              data-testid="titleContainer"
              className={titleContainerClassNames}
            >
              {primaryTitle && (
                <div className={titleClassNames} data-testid="title">
                  <span style={primaryTitleStyles}>{primaryTitle}</span>
                </div>
              )}
              {secondaryTitle && (
                <div className={titleClassNames} data-testid="secondaryTitle">
                  <span style={secondaryTitleStyles}>{secondaryTitle}</span>
                </div>
              )}
            </div>
            {subtitle && (
              <div
                className={subtitleClassNames}
                data-testid="subtitle"
                style={{ color: textColorToUse }}
              >
                {subtitle}
              </div>
            )}
          </div>
          {!!buttonsArray.length && (
            <div data-testid="buttonsContainer">
              {map(buttonsArray, (button) => (
                <ActionButton
                  key={button.destination}
                  button={button}
                  getStyles={getStyles}
                  buttonActions={actions}
                  onClose={this.onClose}
                  actionLabelPrefix={PROMPT}
                  source={PROMPT}
                />
              ))}
            </div>
          )}
        </div>
        {image && (
          <img
            src={image}
            alt="Prompt"
            className={classNames(css.image, imageSizeCss[imageSize], {
              [css.imageLeft]: isImageOnLeft,
            })}
            data-testid="promptImage"
          />
        )}
        {dismissAction && (
          <CloseButton
            className={css.closeIconClassName}
            handleDialogClose={this.onXClose}
          />
        )}
      </div>
    );
  }
}

function mapDispatchToProps(dispatch) {
  const actions = {
    logCategoryActionLabel,
    logWebActivity,
  };

  return {
    promptActions: bindActionCreators(actions, dispatch),
  };
}

export default flow(
  connect(null, mapDispatchToProps),
  withGuideItemTracking,
)(SuperPrompt);
